import { isDefined } from "@prismicio/editor-support/Type"
import { CardContent, Carousel, CarouselItem } from "@prismicio/editor-ui"
import type {
  ImageContent,
  ImageContentView
} from "@prismicio/types-internal/lib/content"
import type { Image } from "@prismicio/types-internal/lib/customtypes"
import { Suspense } from "react"

import {
  getCarouselItemSource,
  getImageLabel,
  getPreviewImage,
  updateImageContentAlt
} from "../../core/imageUtils"
import { getFieldLabel } from "../utils"
import { EmptyCardContent } from "./Components/EmptyCardContent"
import { ImageAltControl } from "./Components/ImageAltControl"
import { ImageFieldCard } from "./Components/ImageFieldCard"
import { useImageField } from "./useImageField"

interface ImageFieldProps {
  content: ImageContent | undefined
  field: Image
  id: string
  onContentChange: (content: ImageContent | undefined) => void
  readOnly: boolean
}

export function ImageField(props: ImageFieldProps) {
  const { content, field, id, onContentChange, readOnly } = props

  /**
   * Variable detailing if origin url has any data.
   * Edge case for default legacy slices as their origin url might be empty
   */
  const hasOriginUrl = content && content.origin.url.length > 0

  const {
    onCropperDialogClose,
    onPageChange,
    onMediaSelected,
    isUpdating,
    page,
    thumbnail,
    allThumbnails,
    allContentViews,
    contentView,
    onClear
  } = useImageField({ field, content, onContentChange })

  const fieldLabel = getFieldLabel(field)

  const images = allThumbnails.map((thumbnail, index) => {
    const contentView = allContentViews?.[index]
    return {
      thumbnail,
      contentView,
      labels: getImageLabel(thumbnail, content, contentView)
    }
  })

  const imageLabel =
    thumbnail && hasOriginUrl
      ? getImageLabel(thumbnail, content, contentView).filter(isDefined)
      : []

  const showCardContent =
    thumbnail && content && hasOriginUrl && allContentViews

  return (
    <ImageFieldCard
      content={content}
      fieldLabel={fieldLabel}
      imageLabel={imageLabel}
      initialOption={page}
      initialImages={images}
      readOnly={readOnly}
      onMediaSelected={onMediaSelected}
      onCropperDialogClose={onCropperDialogClose}
      onClear={onClear}
    >
      <Suspense fallback={<div style={{ height: 466 }} />}>
        {showCardContent ? (
          <NonEmptyCardContent
            allContentViews={allContentViews}
            content={content}
            contentView={contentView}
            id={id}
            onContentChange={onContentChange}
            onPageChange={onPageChange}
            page={page}
            readOnly={readOnly}
            thumbnailName={thumbnail.name}
            isUpdating={isUpdating}
          />
        ) : (
          <EmptyCardContent
            onMediaSelected={onMediaSelected}
            readOnly={readOnly}
            isUpdating={isUpdating}
          />
        )}
      </Suspense>
    </ImageFieldCard>
  )
}

interface NonEmptyCardContentProps {
  allContentViews: readonly (ImageContentView | undefined)[]
  content: ImageContent
  contentView: ImageContentView | undefined
  onPageChange: (page: number) => void
  page: number
  thumbnailName: string
  isUpdating: boolean
  id: string
  readOnly: boolean
  onContentChange: ImageFieldProps["onContentChange"]
}

function NonEmptyCardContent(props: NonEmptyCardContentProps) {
  const {
    allContentViews,
    content,
    contentView,
    id,
    onContentChange,
    onPageChange,
    page,
    readOnly,
    thumbnailName,
    isUpdating
  } = props

  return (
    <CardContent>
      <Carousel
        getItemSource={getCarouselItemSource}
        items={allContentViews}
        onPageChange={onPageChange}
        page={page}
        style={{ height: 400 }}
      >
        {(contentView) =>
          contentView?.url && (
            <CarouselItem
              aria-label="Image Field Item"
              src={getPreviewImage(contentView.url)}
              alt={contentView.alt ?? undefined}
              key={`${thumbnailName}_${contentView.url}`}
              isUpdating={isUpdating}
            />
          )
        }
      </Carousel>
      <ImageAltControl
        id={id}
        readOnly={readOnly || !contentView}
        altText={contentView?.alt ?? ""}
        onValueChange={(value) => {
          if (contentView) {
            onContentChange(
              updateImageContentAlt(thumbnailName, content, contentView, value)
            )
          }
        }}
      />
    </CardContent>
  )
}
